import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

type LoadingHandler =
  | (() => Promise<void> | void)
  | ((event: React.MouseEvent<HTMLButtonElement>) => Promise<void> | void);

type LoadingChildren = (
  handler: ((event: React.MouseEvent<HTMLButtonElement>) => Promise<void> | void) | null,
  isLoading: boolean
) => ReactNode;

type LoadingProps = {
  fn?: LoadingHandler | null;
  children: LoadingChildren;
  afterDelayMs?: number;
};

export const Loading: React.FC<LoadingProps> = ({ fn, children, afterDelayMs = 0 }) => {
  const [isLoading, setIsLoading] = useState(false);
  const inFlightRef = useRef(false);

  const hasEventParam = useMemo(() => {
    if (!fn) {
      return false;
    }

    return fn.length > 0;
  }, [fn]);

  const execute = useCallback(
    async (event?: React.MouseEvent<HTMLButtonElement>) => {
      if (!fn || inFlightRef.current) {
        return;
      }

      inFlightRef.current = true;
      setIsLoading(true);

      try {
        if (hasEventParam) {
          await (fn as (event: React.MouseEvent<HTMLButtonElement>) => Promise<void> | void)(event!);
        } else {
          await (fn as () => Promise<void> | void)();
        }
        if (afterDelayMs > 0) {
          await new Promise(resolve => setTimeout(resolve, afterDelayMs));
        }
      } finally {
        inFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [fn, hasEventParam, afterDelayMs]
  );

  const handlerForChildren = fn
    ? (event: React.MouseEvent<HTMLButtonElement>) => {
        void execute(event);
      }
    : null;

  return <>{children(handlerForChildren, isLoading)} </>;
};

export default Loading;
